import pdfMake from "pdfmake/build/pdfmake";
import vfsFonts from "pdfmake/build/vfs_fonts";
import { NotoSansDevanagari } from "../../../../../NotoSansDevanagariUI-Regular-normal";
import { toast } from "react-toastify";
import { IP } from "../../../../../internalServer";

const setupPdfMakeFonts = () => {
  if (!NotoSansDevanagari) {
    console.error("❌ Font 'NotoSansDevanagari' is undefined.");
    toast.error("Font loading failed. Please try again.");
    return false;
  }
  pdfMake.vfs = vfsFonts;
  pdfMake.vfs["NotoSansDevanagari-Regular.ttf"] = NotoSansDevanagari;

  pdfMake.fonts = {
    Roboto: {
      normal: "Roboto-Regular.ttf",
      bold: "Roboto-Medium.ttf",
    },
    NotoSansDevanagari: {
      normal: "NotoSansDevanagari-Regular.ttf",
      bold: "NotoSansDevanagari-Regular.ttf",
      italics: "NotoSansDevanagari-Regular.ttf",
      bolditalics: "NotoSansDevanagari-Regular.ttf",
    },
  };
  return true;
};

const dateHelper = (isoString) => {
  if (!isoString) return "N/A";
  const date = new Date(isoString);
  const options = { day: "numeric", month: "long", year: "numeric" };
  return date.toLocaleDateString("en-IN", options);
};

// ✅ FIXED: Better error handling + proper blob validation
const convertImageToBase64 = (url) =>
  new Promise((resolve) => {
    console.log("🟡 Trying to load image:", url); // 👈 ADD THIS

    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      console.log("✅ Image loaded:", url); // 👈 ADD THIS

      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;

      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);

      const dataUrl = canvas.toDataURL("image/png");
      resolve(dataUrl.startsWith("data:image/") ? dataUrl : null);
    };

    img.onerror = (err) => {
      console.error("❌ Image load failed:", url, err); // 👈 IMPORTANT
      resolve(null);
    };

    img.src = url;
  });

// ✅ Check if image file
const isImageFile = (filename) => {
  if (!filename) return false;
  const ext = filename.split(".").pop().toLowerCase();
  return ["jpg", "jpeg", "png", "webp", "gif", "bmp"].includes(ext);
};

// ✅ Get filename
const getFileName = (path) => {
  if (!path) return "";
  return path.split("/").pop();
};

const PrintdataQs = async (data, isQuestionBank = false, typeName) => {
  const UserInformation = JSON.parse(localStorage.getItem("UserInformation"));

  const fontsSetup = setupPdfMakeFonts();
  if (!fontsSetup) {
    toast.error("Failed to set up fonts. Cannot generate PDF.");
    return;
  }

  if (!data?.questions?.length) {
    toast.error("No Record Found!");
    return;
  }

  const content = [];

  async function getSchoolLogoBase64() {
    const primaryUrl = UserInformation?.school?.profile_pic
      ? `${IP}${UserInformation?.school?.profile_pic}`
      : null;
    const fallbackUrl = "/images/default_school_logo.jpg";

    try {
      if (primaryUrl && isImageFile(primaryUrl)) {
        const base64 = await convertImageToBase64(primaryUrl);
        if (base64) return base64;
      }
      if (isImageFile(fallbackUrl)) {
        const fallbackBase64 = await convertImageToBase64(fallbackUrl);
        return fallbackBase64;
      }
      return null;
    } catch (err) {
      console.error("Logo error:", err);
      return null;
    }
  }

  const schoolLogoBase64 = await getSchoolLogoBase64();

  content.push({
    columns: [
      { width: "*", text: "" },
      {
        width: "auto",
        gap: 10,
        columns: [
          schoolLogoBase64
            ? { image: schoolLogoBase64, width: 40, margin: [0, 0, 30, 0] }
            : { text: "", width: 40 },
          {
            stack: [
              {
                text: UserInformation?.school?.name || "School Name",
                fontSize: 16,
                bold: true,
                margin: [0, 0, 8, 2],
              },
              {
                text: "Powered By XploreSense™",
                fontSize: 9,
              },
            ],
          },
        ],
      },
      { width: "*", text: "" },
    ],
    margin: [0, -35, 0, 10],
  });

  if (isQuestionBank) {
    content.push({
      columns: [
        {
          text: `QuestionBank Name: ${data.name || "N/A"}`,
          fontSize: 11,
          bold: true,
          color: "#000000",
        },
        {
          text: `Total Marks: ${data.total_marks || "N/A"}`,
          fontSize: 11,
          alignment: "right",
          bold: true,
          color: "#000000",
        },
      ],
      margin: [0, 0, 0, 2],
    });
  } else {
    content.push({
      columns: [
        {
          text: `Subject: ${data.subject_id?.name || "N/A"}`,
          fontSize: 11,
          bold: true,
          color: "#000000",
        },
        {
          text: `Deadline: ${dateHelper(data?.deadline) || "N/A"}`,
          fontSize: 11,
          alignment: "right",
          bold: true,
          color: "#000000",
        },
      ],
      margin: [0, 5, 0, 2],
    });

    content.push({
      columns: [
        {
          text: `Name: ${data.name || "N/A"}`,
          fontSize: 11,
          bold: true,
          color: "#000000",
        },
        {
          text: `Total Marks: ${data.total_marks || "N/A"}`,
          fontSize: 11,
          alignment: "right",
          bold: true,
          color: "#000000",
        },
      ],
      margin: [0, 0, 0, 2],
    });

    content.push({
      text: `Description: ${data.description || "N/A"}`,
      fontSize: 11,
      bold: true,
      color: "#000000",
      margin: [0, 0, 0, 5],
    });
  }

  content.push({
    canvas: [
      {
        type: "line",
        x1: 0,
        y1: 0,
        x2: 515,
        y2: 0,
        lineWidth: 1,
        lineColor: "black",
      },
    ],
    margin: [0, 5, 0, 10],
  });

  // Questions
  for (let i = 0; i < data.questions.length; i++) {
    const q = data.questions[i];
    const questionNumber = `${i + 1}.`;
    const questionTitle = q.title || "N/A";

    content.push({
      columns: [
        {
          width: "auto",
          text: questionNumber,
          font: "NotoSansDevanagari",
          fontSize: 11,
          margin: [0, 10, 5, 0],
        },
        {
          width: "*",
          text: questionTitle,
          font: "NotoSansDevanagari",
          fontSize: 11,
          margin: [0, 10, 0, 0],
        },
        {
          width: "auto",
          text: `[${q.correct_score || 0} Marks]`,
          alignment: "right",
          fontSize: 10,
          bold: true,
          margin: [0, 10, 0, 0],
        },
      ],
    });

    // ✅ Question Attachments - NO SLEEP, proper PDF skip
    if (q.attachments?.length > 0) {
      for (const attachment of q.attachments) {
        if (!attachment) continue;

        // ✅ SKIP PDF/non-image immediately, no fetch
        if (!isImageFile(attachment)) {
          console.log("Skipping non-image:", attachment);
          content.push({
            text: `[Attachment: ${getFileName(attachment)}]`,
            fontSize: 9,
            italics: true,
            color: "#666666",
            margin: [20, 5, 0, 5],
          });
          continue;
        }

        // ✅ Only fetch if image
        try {
          const imageBase64 = await convertImageToBase64(`${IP}${attachment}`);
          console.log("📦 Raw attachment:", attachment);
          console.log("🌐 Full URL:", `${IP}${attachment}`);
          if (imageBase64) {
            content.push({
              image: imageBase64,
              width: 150,
              margin: [20, 5, 0, 5],
            });
          } else {
            content.push({
              text: `[Image: ${getFileName(attachment)}]`,
              fontSize: 9,
              italics: true,
              color: "#999999",
              margin: [20, 5, 0, 5],
            });
          }
        } catch (error) {
          console.log("Image load error:", error);
          content.push({
            text: `[Image: ${getFileName(attachment)}]`,
            fontSize: 9,
            italics: true,
            color: "#999999",
            margin: [20, 5, 0, 5],
          });
        }
      }
    }

    const type = isQuestionBank ? q.type_id?.type : q.type?.type;

    // Options
    if (type === "multiple choice" && q.options?.length) {
      const options = [];

      for (let idx = 0; idx < q.options.length; idx++) {
        const opt = q.options[idx];

        // Option text
        options.push({
          text: `${String.fromCharCode(65 + idx)}. ${opt.value || "N/A"}`,
          font: "NotoSansDevanagari",
          fontSize: 10,
          margin: [0, 5, 0, 0],
        });

        // ✅ Option attachment - NO SLEEP, proper check
        if (opt.attachment) {
          if (!isImageFile(opt.attachment)) {
            // Skip non-image
            options.push({
              text: `[File: ${getFileName(opt.attachment)}]`,
              fontSize: 8,
              italics: true,
              color: "#666666",
              margin: [15, 2, 0, 5],
            });
            continue;
          }

          try {
            const imageBase64 = await convertImageToBase64(
              `${IP}${opt.attachment}`,
            );
            if (imageBase64) {
              options.push({
                image: imageBase64,
                width: 150,
                margin: [15, 5, 0, 5],
              });
            }
          } catch (error) {
            console.log("Option image error:", error);
          }
        }
      }

      content.push({
        stack: options,
        margin: [20, 10, 0, 5],
      });
    }
  }

  const docDefinition = {
    content,
    defaultStyle: {
      font: "NotoSansDevanagari",
    },
    pageMargins: [40, 60, 40, 40],
    footer: (currentPage, pageCount) => ({
      columns: [
        {
          text: `${data?.type_id?.name || data?.name} | ${
            data?.standard_id?.name
          } - ${data.section_id?.name} | ${typeName} `,
          alignment: "left",
          margin: [40, 0, 0, 0],
          fontSize: 8,
        },
        {
          text: `Page ${currentPage} of ${pageCount}`,
          alignment: "right",
          margin: [0, 0, 40, 0],
          fontSize: 8,
        },
      ],
    }),
  };

  try {
    pdfMake.createPdf(docDefinition).download(`${data.name}.pdf`);
    toast.success("PDF generated successfully!");
  } catch (error) {
    console.error("PDF Error:", error);
    toast.error("Failed to generate PDF: " + error.message);
  }
};

export default PrintdataQs;
